import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { useTheme } from '@/hooks/useTheme';
import { useAuthStore } from '@/store/authStore';
import { projectService } from '@/services/projectService';
import { getProjectMapImageUrl } from '@/utils';
import { Toast } from '@/components/Toast';
import type { AxiosError } from 'axios';

export function ProjectImagePage() {
  const { projectId } = useParams<{ projectId: string }>();
  const id = projectId ? Number(projectId) : NaN;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isDark, toggleTheme } = useTheme();
  const { user, isRoot, isAdmin } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  const [showToast, setShowToast] = useState(false);

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: () => projectService.getProject(id),
    enabled: Number.isFinite(id),
    retry: 1,
  });

  useEffect(() => {
    if (project?.mapImage) {
      setPreview(getProjectMapImageUrl(project.mapImage));
    } else {
      setPreview(null);
    }
  }, [project]);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => projectService.uploadImage(id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setToastMessage('Image uploadée avec succès.');
      setToastType('success');
      setShowToast(true);
    },
    onError: () => {
      setToastMessage("Erreur lors de l'upload de l'image.");
      setToastType('error');
      setShowToast(true);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => projectService.deleteImage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setPreview(null);
      setIsDeleteConfirmOpen(false);
      setToastMessage('Image supprimée.');
      setToastType('success');
      setShowToast(true);
    },
    onError: () => {
      setIsDeleteConfirmOpen(false);
      setToastMessage("Erreur lors de la suppression de l'image.");
      setToastType('error');
      setShowToast(true);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      alert('Type de fichier non autorisé. Formats acceptés : JPEG, PNG, GIF, WebP, SVG');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('Le fichier est trop volumineux. Taille maximale : 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result as string);
    reader.readAsDataURL(file);

    uploadMutation.mutate(file);
  };

  const getUserInitials = (name: string) =>
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  const headerTitle = useMemo(() => {
    if (!project) return 'Image';
    return `Image • ${project.title}`;
  }, [project]);

  if (!isRoot() && !isAdmin()) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-500">Accès refusé. Vous devez être administrateur.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-slate-500 dark:text-slate-400">Chargement...</p>
        </div>
      </div>
    );
  }

  // If the query was disabled due to an invalid route param
  if (!Number.isFinite(id)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <p className="text-red-500">Identifiant projet invalide</p>
          <button onClick={() => navigate('/admin/projects')} className="mt-4 text-primary hover:text-primary-600">
            Retour aux projets
          </button>
        </div>
      </div>
    );
  }

  if (!project) {
    // React Query returns `undefined` on error; display a better message than "not found"
    const maybeError = (queryClient.getQueryState(['project', id])?.error ?? null) as AxiosError | null;
    const status = maybeError?.response?.status;

    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <p className="text-red-500">
            {status === 404
              ? 'Projet non trouvé'
              : status === 403
                ? 'Accès refusé à ce projet'
                : 'Erreur lors du chargement du projet'}
            {status ? ` (HTTP ${status})` : null}
          </p>
          <button onClick={() => navigate('/admin/projects')} className="mt-4 text-primary hover:text-primary-600">
            Retour aux projets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      <AdminSidebar getUserInitials={getUserInitials} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="flex items-center gap-4 md:hidden">
            <button onClick={() => setSidebarOpen(true)} className="text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-white">
              <span className="material-icons-outlined">menu</span>
            </button>
            <span className="font-bold text-lg dark:text-white">GTB Admin</span>
          </div>
          <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
            <span className="text-slate-400 dark:text-slate-600">Admin</span> / Projets / Image
          </div>
          {project && (
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-slate-900 dark:text-white">{project.title}</span>
              {project.city && (
                <>
                  <span className="text-slate-400">•</span>
                  <span className="text-sm text-slate-500 dark:text-slate-400">{project.city}</span>
                </>
              )}
            </div>
          )}
          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? <span className="material-icons-outlined">light_mode</span> : <span className="material-icons-outlined">dark_mode</span>}
            </button>
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-full bg-gradient-to-tr from-primary to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                {user?.fullName ? getUserInitials(user.fullName) : 'AD'}
              </div>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{headerTitle}</h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Uploade l’image utilisée comme fond sur la carte côté utilisateur.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate(`/admin/projects/${project.id}/edit`)}
                className="inline-flex items-center px-4 py-2 border border-slate-200 dark:border-[#1E293B] rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-[#16202A] hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors"
              >
                <span className="material-icons-outlined text-lg mr-2">edit</span>
                Éditer
              </button>
            </div>
          </div>

          <div className="bg-white dark:bg-[#16202A] shadow-sm rounded-xl border border-slate-200 dark:border-[#1E293B] overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-6 flex-col lg:flex-row">
                <div className="w-full lg:w-2/3">
                  <div className="aspect-[16/9] w-full rounded-xl border border-slate-200 dark:border-[#1E293B] bg-slate-50 dark:bg-[#0B1116] overflow-hidden flex items-center justify-center">
                    {preview ? (
                      <img src={preview} alt="Aperçu" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center text-slate-500 dark:text-slate-400">
                        <span className="material-icons-outlined text-5xl mb-2">image</span>
                        <div>Aucune image configurée</div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="w-full lg:w-1/3">
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadMutation.isPending}
                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary hover:bg-sky-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="material-icons-outlined text-lg mr-2">cloud_upload</span>
                    {uploadMutation.isPending ? 'Upload…' : 'Uploader une image'}
                  </button>
                  <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                    Formats: JPEG/PNG/GIF/WebP/SVG • Taille max: 10MB
                  </div>

                  <button
                    onClick={() => setIsDeleteConfirmOpen(true)}
                    disabled={!project.mapImage || deleteMutation.isPending}
                    className="mt-4 w-full inline-flex items-center justify-center px-4 py-2 border border-slate-200 dark:border-[#1E293B] rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-[#16202A] hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="material-icons-outlined text-lg mr-2">delete</span>
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Delete confirmation modal */}
      {isDeleteConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => (!deleteMutation.isPending ? setIsDeleteConfirmOpen(false) : null)}
          />
          <div className="relative w-full max-w-md rounded-xl bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] shadow-xl">
            <div className="p-5">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-red-500/10 text-red-600 dark:text-red-400 flex items-center justify-center border border-red-500/20">
                  <span className="material-icons-outlined">warning</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-slate-900 dark:text-white">Supprimer l’image ?</h3>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Cette action est irréversible. L’image de fond de la carte sera supprimée.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 pb-5 flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setIsDeleteConfirmOpen(false)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 rounded-lg border border-transparent bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
              >
                {deleteMutation.isPending ? (
                  <>
                    <span className="material-icons-outlined animate-spin mr-2" style={{ fontSize: '18px' }}>
                      progress_activity
                    </span>
                    Suppression…
                  </>
                ) : (
                  'Supprimer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
}

